import React, { useState, useEffect, useCallback, useRef } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Loader2, Plus, ChevronRight, Car, ArrowLeft, Camera, X, Upload, Mail, Trash2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle
} from '@/components/ui/sheet';
import {
  Dialog, DialogContent
} from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};
const imgUrl = (u) => u?.startsWith('/') ? `${BACKEND}${u}` : u;

const STATUS_CONFIG = {
  nieuw:           { label: 'Nieuw',          dot: 'bg-red-500',    badge: 'bg-red-100 text-red-700' },
  in_behandeling:  { label: 'In behandeling', dot: 'bg-amber-500',  badge: 'bg-amber-100 text-amber-700' },
  afgehandeld:     { label: 'Afgehandeld',    dot: 'bg-green-500',  badge: 'bg-green-100 text-green-700' }
};

const fmtDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

// ── Photo Lightbox ──────────────────────────────────────────────────────────
const PhotoLightbox = ({ url, onClose }) => (
  <Dialog open={!!url} onOpenChange={onClose}>
    <DialogContent className="max-w-3xl p-2">
      {url && <img src={imgUrl(url)} alt="Foto" className="w-full rounded-lg" />}
    </DialogContent>
  </Dialog>
);

// ── Detail Panel (Sheet) ────────────────────────────────────────────────────
const DetailPanel = ({ melding, onClose, onSaved, isAdmin, magVerwijderen }) => {
  const [saving, setSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [notitie, setNotitie] = useState(melding?.notitie || '');

  useEffect(() => {
    setNotitie(melding?.notitie || '');
  }, [melding]);

  const handleStatusChange = async (newStatus) => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/schademeldingen/${melding.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...hdrs() },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        toast.success('Status bijgewerkt');
        onSaved?.();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Status wijzigen mislukt');
      }
    } catch {
      toast.error('Status wijzigen mislukt');
    } finally {
      setSaving(false);
    }
  };

  const handleNotitieOpslaan = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/schademeldingen/${melding.id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...hdrs() },
        body: JSON.stringify({ status: melding.status, notitie })
      });
      if (res.ok) {
        toast.success('Notitie opgeslagen');
        onSaved?.();
      } else {
        toast.error('Opslaan mislukt');
      }
    } catch {
      toast.error('Opslaan mislukt');
    } finally {
      setSaving(false);
    }
  };

  if (!melding) return null;

  const cfg = STATUS_CONFIG[melding.status] || STATUS_CONFIG.nieuw;

  const Field = ({ label, value }) => (
    <div className="flex justify-between py-2 border-b border-slate-50 last:border-0">
      <span className="text-slate-500 text-sm">{label}</span>
      <span className="text-slate-800 text-sm font-medium text-right max-w-[60%] break-words">{value || '-'}</span>
    </div>
  );

  return (
    <>
      <Sheet open={!!melding} onOpenChange={onClose}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="detail-panel">
          <div className="space-y-5">
            <SheetHeader>
              <SheetTitle className="text-lg flex items-center gap-2">
                <Car className="w-5 h-5" /> Schademelding
              </SheetTitle>
              <div className="flex items-center gap-2 mt-1">
                <Badge className={`text-xs ${cfg.badge}`} data-testid="detail-status-badge">{cfg.label}</Badge>
                <span className="text-sm text-slate-500">{fmtDate(melding.datum)}</span>
              </div>
            </SheetHeader>

            {/* Voertuig & Klant */}
            <div>
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">Voertuig & Transportbedrijf</h3>
              <div className="bg-slate-50 rounded-lg p-3">
                <Field label="Bedrijfsnaam" value={melding.bedrijfsnaam} />
                <Field label="Naam chauffeur" value={melding.naam_chauffeur} />
                <Field label="Telefoonnummer" value={melding.telefoonnummer} />
                <Field label="Kenteken(s)" value={melding.kentekens} />
                <Field label="Email transportbedrijf" value={melding.email_bestuurder} />
                <Field label="CC" value={(melding.cc_emails || []).join(', ') || '-'} />
              </div>
            </div>

            {/* Schade details */}
            <div>
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">Schade</h3>
              <div className="bg-slate-50 rounded-lg p-3 space-y-2">
                <Field label="Locatie" value={melding.locatie_naam} />
                {melding.klantnummer_bovemij && <Field label="Klantnr. Bovemij" value={melding.klantnummer_bovemij} />}
                <Field label="Medewerker" value={melding.medewerker_naam} />
                <Field label="Email locatie" value={melding.email_locatie} />
                <div className="pt-2">
                  <span className="text-slate-500 text-sm block mb-1">Wat is er gebeurd?</span>
                  <p className="text-slate-800 text-sm whitespace-pre-wrap">{melding.wat_gebeurd || '-'}</p>
                </div>
              </div>
            </div>

            {/* Foto's */}
            {melding.foto_urls?.length > 0 && (
              <div>
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">Foto's van schade</h3>
                <div className="grid grid-cols-3 gap-2">
                  {melding.foto_urls.map((url, i) => (
                    <button
                      key={i}
                      onClick={() => setLightbox(url)}
                      className="aspect-square rounded-lg border border-slate-200 overflow-hidden hover:ring-2 ring-blue-400 transition-all"
                      data-testid={`photo-thumb-${i}`}
                    >
                      <img src={imgUrl(url)} alt={`Foto ${i + 1}`} loading="lazy"
                        decoding="async" width="160" height="160"
                        className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Status update (admin only) */}
            {isAdmin && (
              <div>
                <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">Status wijzigen</h3>
                <Select
                  value={melding.status}
                  onValueChange={handleStatusChange}
                  disabled={saving}
                >
                  <SelectTrigger className="w-full" data-testid="status-change-dropdown">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="nieuw">Nieuw</SelectItem>
                    <SelectItem value="in_behandeling">In behandeling</SelectItem>
                    <SelectItem value="afgehandeld">Afgehandeld</SelectItem>
                  </SelectContent>
                </Select>
                {saving && <p className="text-xs text-slate-400 mt-1">Opslaan...</p>}
              </div>
            )}

            {/* Notitie (admin: editable, bedrijfsleider: read-only) */}
            <div>
              <h3 className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-2">Interne notitie</h3>
              {isAdmin ? (
                <div className="space-y-2">
                  <Textarea
                    value={notitie}
                    onChange={(e) => setNotitie(e.target.value)}
                    rows={3}
                    placeholder="Interne notitie toevoegen..."
                    data-testid="notitie-textarea"
                  />
                  <Button
                    size="sm"
                    onClick={handleNotitieOpslaan}
                    disabled={saving || notitie === (melding.notitie || '')}
                    className="bg-[#1f2548] hover:bg-[#2a3060]"
                    data-testid="notitie-opslaan-btn"
                  >
                    Notitie opslaan
                  </Button>
                </div>
              ) : (
                <p className="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-lg p-3" data-testid="notitie-readonly">
                  {melding.notitie || <span className="text-slate-400 italic">Geen notitie</span>}
                </p>
              )}
            </div>

            {/* Meta */}
            <div className="text-xs text-slate-400 pt-2 border-t border-slate-100">
              <p>Aangemaakt: {fmtDate(melding.created_at)}</p>
              {melding.updated_at !== melding.created_at && <p>Laatst bijgewerkt: {fmtDate(melding.updated_at)}</p>}
            </div>

            {/* Verwijderen: alleen admin — de backend staat niets anders toe (4.8c) */}
            {magVerwijderen && (
              <div className="pt-2 border-t border-slate-100">
                <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-600 hover:bg-red-50 w-full"
                  onClick={async () => {
                    if (!confirm('Weet je zeker dat je deze schademelding wilt verwijderen? Dit kan niet ongedaan worden.')) return;
                    try {
                      const res = await fetch(`${API}/schademeldingen/${melding.id}`, { method: 'DELETE', headers: hdrs() });
                      if (res.ok) { toast.success('Schademelding verwijderd'); onClose(); onSaved?.(); }
                      else toast.error('Verwijderen mislukt');
                    } catch { toast.error('Verwijderen mislukt'); }
                  }} data-testid="admin-delete-schade">
                  <Trash2 className="w-3.5 h-3.5 mr-1" /> Schademelding verwijderen
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
      <PhotoLightbox url={lightbox} onClose={() => setLightbox(null)} />
    </>
  );
};

// ── Photo Upload Component ──────────────────────────────────────────────────
const PhotoUploader = ({ photos, onPhotosChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (files) => {
    if (photos.length + files.length > 5) {
      toast.error('Maximaal 5 foto\'s toegestaan');
      return;
    }
    setUploading(true);
    const newUrls = [...photos];
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is te groot (max 10MB)`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await fetch(`${API}/schademeldingen/upload-foto`, {
          method: 'POST',
          headers: hdrs(),
          body: fd
        });
        if (res.ok) {
          const { url } = await res.json();
          newUrls.push(url);
        } else {
          toast.error(`Upload mislukt: ${file.name}`);
        }
      } catch {
        toast.error(`Upload mislukt: ${file.name}`);
      }
    }
    onPhotosChange(newUrls);
    setUploading(false);
  };

  const removePhoto = (idx) => {
    onPhotosChange(photos.filter((_, i) => i !== idx));
  };

  return (
    <div>
      <Label>Foto's van schade (max 5)</Label>
      <div className="mt-2 flex gap-2 flex-wrap">
        {photos.map((url, i) => (
          <div key={i} className="relative w-20 h-20 rounded-lg border border-slate-200 overflow-hidden group">
            <img src={imgUrl(url)} alt="" loading="lazy" decoding="async"
              className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removePhoto(i)}
              className="absolute top-0.5 right-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
              data-testid={`remove-photo-${i}`}
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ))}
        {photos.length < 5 && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex flex-col items-center justify-center text-slate-400 hover:border-[#1f2548] hover:text-[#1f2548] transition-colors"
            data-testid="add-photo-btn"
          >
            {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Camera className="w-5 h-5" /><span className="text-[10px] mt-0.5">Foto</span></>}
          </button>
        )}
      </div>
      <input
        ref={fileRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp"
        multiple
        className="hidden"
        onChange={(e) => e.target.files?.length && handleFiles(Array.from(e.target.files))}
      />
    </div>
  );
};

// ── New Melding Form (Full Page) ────────────────────────────────────────────
const NieuweMeldingForm = ({ locatieId, locatieData, userName, onClose, onSaved }) => {
  const bovemijNr = locatieData?.bovemij_klantnummer || '';
  const [form, setForm] = useState({
    datum: new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' }),
    bedrijfsnaam: '',
    naam_chauffeur: '',
    telefoonnummer: '',
    kentekens: '',
    medewerker_naam: userName || '',
    wat_gebeurd: '',
    email_bestuurder: '',
    cc_emails: '',
  });
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  const set = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bedrijfsnaam || !form.naam_chauffeur || !form.telefoonnummer || !form.kentekens || !form.medewerker_naam || !form.wat_gebeurd) {
      toast.error('Vul alle verplichte velden in');
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/schademeldingen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...hdrs() },
        body: JSON.stringify({
          locatie_id: locatieId,
          ...form,
          foto_urls: photos
        })
      });
      if (res.ok) {
        toast.success('Schademelding ingediend');
        onSaved?.();
        onClose();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Indienen mislukt');
      }
    } catch {
      toast.error('Indienen mislukt');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="nieuwe-melding-form">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onClose} data-testid="form-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Terug
        </Button>
        <h1 className="text-xl font-bold text-[#1f2548]">Nieuwe schademelding</h1>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-xl border border-slate-200 p-6 space-y-5 max-w-2xl">
        {/* Datum */}
        <div>
          <Label htmlFor="datum">Datum *</Label>
          <Input id="datum" type="date" value={form.datum} onChange={set('datum')} required data-testid="input-datum" className="mt-1" />
        </div>

        {/* Persoonsgegevens */}
        <div className="space-y-1">
          <p className="text-sm font-semibold text-[#1f2548]">Gegevens betrokken partij</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="bedrijfsnaam">Bedrijfsnaam *</Label>
              <Input id="bedrijfsnaam" value={form.bedrijfsnaam} onChange={set('bedrijfsnaam')} required placeholder="Bedrijfsnaam" data-testid="input-bedrijfsnaam" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="naam_chauffeur">Naam chauffeur *</Label>
              <Input id="naam_chauffeur" value={form.naam_chauffeur} onChange={set('naam_chauffeur')} required placeholder="Naam chauffeur" data-testid="input-chauffeur" className="mt-1" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
            <div>
              <Label htmlFor="telefoonnummer">Telefoonnummer chauffeur/bedrijf *</Label>
              <Input id="telefoonnummer" value={form.telefoonnummer} onChange={set('telefoonnummer')} required placeholder="06-12345678" data-testid="input-telefoon" className="mt-1" />
            </div>
            <div>
              <Label htmlFor="kentekens">Kenteken(s) *</Label>
              <Input id="kentekens" value={form.kentekens} onChange={set('kentekens')} required placeholder="AB-123-CD" data-testid="input-kentekens" className="mt-1" />
            </div>
          </div>
        </div>

        {/* Wat gebeurd */}
        <div>
          <Label htmlFor="wat_gebeurd">Wat is er gebeurd? *</Label>
          <Textarea id="wat_gebeurd" value={form.wat_gebeurd} onChange={set('wat_gebeurd')} rows={4} required placeholder="Beschrijf zo duidelijk mogelijk wat er is gebeurd..." data-testid="input-wat-gebeurd" className="mt-1" />
        </div>

        {/* Photo upload */}
        <PhotoUploader photos={photos} onPhotosChange={setPhotos} />

        {/* Medewerker */}
        <div>
          <Label htmlFor="medewerker_naam">Naam medewerker Truckwash *</Label>
          <Input id="medewerker_naam" value={form.medewerker_naam} onChange={set('medewerker_naam')} required placeholder="Naam medewerker" data-testid="input-medewerker" className="mt-1" />
        </div>

        {/* Bovemij Klantnummer (read-only) */}
        {bovemijNr && (
          <div>
            <Label>Klantnummer Bovemij (verzekering)</Label>
            <Input value={bovemijNr} readOnly disabled className="mt-1 bg-slate-50 text-slate-600" data-testid="input-bovemij-klantnummer" />
          </div>
        )}

        {/* Email bestuurder */}
        <div>
          <Label htmlFor="email_bestuurder">Email Bestuurder/Transportbedrijf</Label>
          <Input id="email_bestuurder" type="email" value={form.email_bestuurder} onChange={set('email_bestuurder')} placeholder="chauffeur@transportbedrijf.nl" data-testid="input-email-bestuurder" className="mt-1" />
          <p className="text-xs text-slate-400 mt-1">Indien ingevuld ontvangt de bestuurder een bevestigingsmail</p>
        </div>

        {/* CC email */}
        <div>
          <Label htmlFor="cc_emails">CC e-mailadres (optioneel)</Label>
          <Input id="cc_emails" type="text" value={form.cc_emails} onChange={set('cc_emails')} placeholder="email@voorbeeld.nl, email2@voorbeeld.nl" data-testid="input-cc-emails" className="mt-1" />
          <p className="text-xs text-slate-400 mt-1">Meerdere adressen scheiden met een komma</p>
        </div>

        {/* Disclaimer */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="disclaimer">
          <strong>Let op:</strong> Dit is slechts een melding, geen schuldbekentenis. Hier kunnen geen rechten aan worden ontleend.
        </div>

        {/* Submit */}
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>Annuleren</Button>
          <Button type="submit" disabled={saving} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="submit-melding-btn">
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Melding indienen
          </Button>
        </div>
      </form>
    </div>
  );
};

// ── Main Page ───────────────────────────────────────────────────────────────
const Schademeldingen = () => {
  const { selectedLocatie, selectedLocatieData } = useOutletContext();
  const { user, isAdmin, canManage } = useAuth();
  const [searchParams] = useSearchParams();
  const admin = canManage();

  const [meldingen, setMeldingen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('alle');
  const [alleLocaties, setAlleLocaties] = useState(false);
  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);

  // If navigated from management with locatie_id param
  const filterLocatieId = searchParams.get('locatie_id');

  const fetchMeldingen = useCallback(async () => {
    setLoading(true);
    try {
      // 4.3: ook managers/admins zien standaard alleen de GESELECTEERDE locatie;
      // "Alle locaties" is een bewuste keuze.
      const params = new URLSearchParams();
      const locatie = filterLocatieId || selectedLocatie;
      if (!(admin && alleLocaties && !filterLocatieId)) {
        if (!locatie) {
          setMeldingen([]);
          setLoading(false);
          return;
        }
        params.set('locatie_id', locatie);
      }
      if (filter !== 'alle') params.set('status', filter);

      const res = await fetch(`${API}/schademeldingen?${params}`, { headers: hdrs() });
      if (res.ok) setMeldingen(await res.json());
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedLocatie, filter, admin, filterLocatieId, alleLocaties]);

  useEffect(() => { fetchMeldingen(); }, [fetchMeldingen]);

  const nieuwCount = meldingen.filter(m => m.status === 'nieuw').length;
  const openCount = meldingen.filter(m => ['nieuw', 'in_behandeling'].includes(m.status)).length;

  // Show form view
  if (showForm) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-0">
        <NieuweMeldingForm
          locatieId={selectedLocatie}
          locatieData={selectedLocatieData}
          userName={user?.naam || ''}
          onClose={() => setShowForm(false)}
          onSaved={() => { fetchMeldingen(); setShowForm(false); }}
        />
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 sm:px-0" data-testid="schademeldingen-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[#1f2548] flex items-center gap-2" data-testid="schademeldingen-title">
            <Car className="w-6 h-6" /> Schademeldingen
          </h1>
          <p className="text-slate-500" data-testid="schade-scope">
            {filterLocatieId ? 'Gefilterd op locatie' :
             admin && alleLocaties ? 'Alle locaties' :
             selectedLocatieData?.naam || 'Selecteer een locatie'}
          </p>
        </div>
        <Button onClick={() => setShowForm(true)} className="bg-[#1f2548] hover:bg-[#2a3060]" data-testid="nieuwe-melding-btn">
          <Plus className="w-4 h-4 mr-2" /> Nieuwe schademelding indienen
        </Button>
      </div>

      {/* Filters */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger className="w-[180px]" data-testid="status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="alle">Alle statussen</SelectItem>
            <SelectItem value="nieuw">Nieuw</SelectItem>
            <SelectItem value="in_behandeling">In behandeling</SelectItem>
            <SelectItem value="afgehandeld">Afgehandeld</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-3">
          {admin && !filterLocatieId && (
            <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
              <input type="checkbox" checked={alleLocaties} className="accent-[#1f2548]"
                onChange={(e) => setAlleLocaties(e.target.checked)}
                data-testid="alle-locaties-switch" />
              Alle locaties
            </label>
          )}
          {openCount > 0 && (
            <Badge className="bg-red-100 text-red-700" data-testid="nieuw-count-badge">
              {openCount} open{nieuwCount > 0 ? `, waarvan ${nieuwCount} nieuw` : ''}
            </Badge>
          )}
        </div>
      </div>

      {/* List */}
      <div className="space-y-3" data-testid="meldingen-list">
        {meldingen.map((m) => {
          const cfg = STATUS_CONFIG[m.status] || STATUS_CONFIG.nieuw;
          return (
            <button
              key={m.id}
              onClick={() => setSelected(m)}
              className="w-full bg-white rounded-xl border border-slate-200 p-4 text-left hover:shadow-md transition-all"
              data-testid={`melding-card-${m.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 min-w-0">
                  <span className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 ${cfg.dot}`} />
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm text-slate-500">{fmtDate(m.datum)}</span>
                      {admin && m.locatie_naam && (
                        <span className="text-sm font-medium text-[#1f2548]">{m.locatie_naam}</span>
                      )}
                      {m.bedrijfsnaam && <span className="text-sm text-slate-600">{m.bedrijfsnaam}</span>}
                      {m.kentekens && <span className="text-sm font-mono font-semibold text-slate-700">{m.kentekens}</span>}
                    </div>
                    <p className="text-slate-700 mt-0.5 line-clamp-1">{m.wat_gebeurd || 'Geen omschrijving'}</p>
                    <Badge className={`mt-1 text-xs ${cfg.badge}`}>{cfg.label}</Badge>
                  </div>
                </div>
                <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 mt-1" />
              </div>
            </button>
          );
        })}

        {meldingen.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400" data-testid="meldingen-empty">
            Geen schademeldingen gevonden
          </div>
        )}
      </div>

      {/* Detail panel */}
      {selected && (
        <DetailPanel
          melding={selected}
          onClose={() => setSelected(null)}
          onSaved={() => { fetchMeldingen(); setSelected(null); }}
          isAdmin={admin}
          magVerwijderen={isAdmin()}
        />
      )}
    </div>
  );
};

export default Schademeldingen;
